import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { Trophy, Globe, Lock, CreditCard, Users } from 'lucide-react';
import Swal from 'sweetalert2';

function useMobile() {
    const [mobile, setMobile] = useState(window.innerWidth < 700);
    useEffect(() => {
        const h = () => setMobile(window.innerWidth < 700);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);
    return mobile;
}

function CompCard({ comp, onSelect, isPublicMode, isShared = false }) {
    return (
        <div
            onClick={() => onSelect(comp)}
            style={{
                padding: '20px', backgroundColor: 'white', borderRadius: '12px', cursor: 'pointer',
                border: isShared ? '1px solid #d6bcfa' : '1px solid #e2e8f0',
                transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '10px'
            }}
        >
            {isShared && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', color: '#805ad5', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Users size={12} /> Partagée avec moi
                </div>
            )}
            <h3 style={{ margin: 0 }}>{comp.name}</h3>
            <div style={{ fontSize: '13px', color: '#4a5568' }}>📍 {comp.address || "Lieu non précisé"}</div>
            <div style={{ fontSize: '13px', color: '#4a5568' }}>
                📅 {comp.event_date ? new Date(comp.event_date).toLocaleDateString('fr-FR') : "Date non précisée"}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#718096', borderTop: '1px solid #edf2f7', paddingTop: '10px', marginTop: '5px' }}>
                {comp.visibility === 'public' ? <Globe size={14} color="#38a169"/> : <Lock size={14}/>}
                <span>{isPublicMode ? "Suivre le direct →" : "Gérer le plateau →"}</span>
            </div>
        </div>
    );
}

// On ajoute 'mode' aux props (soit 'admin' par défaut, soit 'public')
export default function CompetitionDashboard({ user, onSelect, onShowLogin, onNavigate, mode = "admin" }) {
    const [competitions, setCompetitions] = useState([]);
    const [sharedCompetitions, setSharedCompetitions] = useState([]);
    const [newComp, setNewComp] = useState({ name: "", address: "", date: "" });
    const [credits, setCredits] = useState(0);
    const [loadingCredits, setLoadingCredits] = useState(false);
    const isPublicMode = mode === "public";
    const isMobile = useMobile();

    useEffect(() => {
        fetchCompetitions();
        if (!isPublicMode && user) {
            fetchCredits();
        }
    }, [user, mode]);

    const fetchCredits = async () => {
        setLoadingCredits(true);
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('credits')
                .eq('id', user.id)
                .single();
            if (error) throw error;
            setCredits(data?.credits || 0);
        } catch (err) {
            console.error("Erreur crédits:", err);
        } finally {
            setLoadingCredits(false);
        }
    };

    const fetchCompetitions = async () => {
        if (mode === "public") {
            const { data, error } = await supabase
                .from('competitions')
                .select('*')
                .eq('visibility', 'public')
                .order('created_at', { ascending: false });
            if (error) console.error("Erreur de chargement:", error);
            else setCompetitions(data);
            return;
        }

        if (!user) return;

        // Mes compétitions (propriétaire)
        const { data: owned, error: ownedError } = await supabase
            .from('competitions')
            .select('*')
            .eq('owner_id', user.id)
            .order('created_at', { ascending: false });

        if (ownedError) console.error("Erreur compétitions propres:", ownedError);
        else setCompetitions(owned || []);

        // Compétitions partagées (co-gestionnaire ou co-propriétaire)
        const [{ data: sharedAsMgr }, { data: sharedAsOwner }] = await Promise.all([
            supabase.from('competitions').select('*').contains('co_managers', [user.email]).order('created_at', { ascending: false }),
            supabase.from('competitions').select('*').contains('co_owners', [user.email]).order('created_at', { ascending: false })
        ]);
        const merged = [...(sharedAsMgr || []), ...(sharedAsOwner || [])];
        const unique = merged.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
        setSharedCompetitions(unique);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newComp.name.trim() || !newComp.address.trim() || !newComp.date || !user) {
            Swal.fire({ icon: 'warning', title: 'Champs requis', text: 'Veuillez remplir le nom, l\'adresse et la date.' });
            return;
        }

        const result = await Swal.fire({
            title: 'Créer la compétition ?',
            text: "Pendant la phase bêta, la création est gratuite et illimitée.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Confirmer',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#3182ce'
        });

        if (result.isConfirmed) {
            try {
                Swal.showLoading();
                
                // Créer la compétition
                const { error: createError } = await supabase
                    .from('competitions')
                    .insert([{ 
                        name: newComp.name, 
                        address: newComp.address,
                        event_date: newComp.date,
                        owner_id: user.id 
                    }])
                    .select();

                if (createError) throw createError;

                setNewComp({ name: "", address: "", date: "" });
                fetchCompetitions();
                Swal.fire('Succès', 'Compétition créée ! Merci d\'utiliser SavatEvent.', 'success');
            } catch (err) {
                console.error(err);
                Swal.fire('Erreur', 'Impossible de créer la compétition : ' + err.message, 'error');
            }
        }
    };

    return (
        <div style={{ padding: isMobile ? '20px 15px' : '40px', maxWidth: '900px', margin: '0 auto' }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', flexWrap: 'wrap', gap: '20px' }}>
                <h1 style={{ display: 'flex', alignItems: 'center', gap: '15px', margin: 0 }}>
                    <Trophy size={32} color="#3182ce" /> 
                    {isPublicMode ? "Compétitions Publiques" : "Mes Compétitions"}
                </h1>

                {!isPublicMode && user && (
                    <div style={{ 
                        display: 'flex', alignItems: 'center', gap: '15px', 
                        backgroundColor: '#ebf8ff', padding: '10px 20px', 
                        borderRadius: '12px', border: '1px solid #bee3f8' 
                    }}>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', color: '#2c5282', fontWeight: 'bold', textTransform: 'uppercase' }}>Crédits</div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2b6cb0' }}>{credits}</div>
                        </div>
                        {credits === 0 && (
                            <button 
                                onClick={() => onNavigate('parametres')}
                                style={{ 
                                    backgroundColor: '#3182ce', color: 'white', padding: '6px 12px', 
                                    borderRadius: '6px', border: 'none', cursor: 'pointer', 
                                    fontSize: '11px', fontWeight: 'bold' 
                                }}
                            >
                                Recharger
                            </button>
                        )}
                    </div>
                )}
            </header>

            {!isPublicMode && user && (
                <div 
                    style={{ 
                        backgroundColor: '#ebf8ff', border: '1px solid #90cdf4', 
                        color: '#2c5282', padding: '15px 20px', borderRadius: '10px', 
                        marginBottom: '30px', display: 'flex', 
                        alignItems: 'center', gap: '12px' 
                    }}
                >
                    <Trophy size={20} color="#3182ce" />
                    <span style={{ fontWeight: '600' }}>🎁 Mode Bêta : L'application est actuellement gratuite. Profitez-en pour organiser vos tournois !</span>
                </div>
            )}

            {/* ON AFFICHE LE FORMULAIRE UNIQUEMENT EN MODE ADMIN */}
            {!isPublicMode && user && (
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Nouvel évènement</h3>
                    <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#4a5568' }}>Nom *</label>
                                <input 
                                    value={newComp.name} 
                                    onChange={(e) => setNewComp({...newComp, name: e.target.value})} 
                                    placeholder="Nom du tournoi..." 
                                    style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e0' }} 
                                    required 
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#4a5568' }}>Lieu / Adresse *</label>
                                <input 
                                    value={newComp.address} 
                                    onChange={(e) => setNewComp({...newComp, address: e.target.value})} 
                                    placeholder="Ville, gymnase..." 
                                    style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e0' }} 
                                    required 
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#4a5568' }}>Date *</label>
                                <input 
                                    type="date"
                                    value={newComp.date} 
                                    onChange={(e) => setNewComp({...newComp, date: e.target.value})} 
                                    style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e0' }} 
                                    required 
                                />
                            </div>
                        </div>
                        <button type="submit" style={{ backgroundColor: '#3182ce', color: 'white', padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', alignSelf: isMobile ? 'stretch' : 'flex-end', width: isMobile ? '100%' : '200px' }}>
                            Créer la compétition
                        </button>
                    </form>
                </div>
            )}

            {/* MESSAGE SI SPECTATEUR NON CONNECTÉ SUR "MES COMPÉTITIONS" */}
            {!user && !isPublicMode ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Connectez-vous pour gérer vos tournois.</p>
                    <button onClick={onShowLogin}>Se connecter</button>
                </div>
            ) : (
                <>
                    {/* MES COMPÉTITIONS */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                        {competitions.length === 0 && !isPublicMode && (
                            <p style={{ color: '#718096' }}>Aucune compétition trouvée.</p>
                        )}
                        {competitions.map(comp => (
                            <CompCard key={comp.id} comp={comp} onSelect={onSelect} isPublicMode={isPublicMode} />
                        ))}
                    </div>

                    {/* COMPÉTITIONS PARTAGÉES */}
                    {!isPublicMode && sharedCompetitions.length > 0 && (
                        <div style={{ marginTop: '40px' }}>
                            <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', fontSize: '20px', color: '#2d3748' }}>
                                <CreditCard size={22} color="#805ad5" /> Compétitions partagées
                            </h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                                {sharedCompetitions.map(comp => (
                                    <CompCard key={comp.id} comp={comp} onSelect={onSelect} isPublicMode={false} isShared />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
